/**
 * Shared toast notification — one at a time, auto-dismisses.
 * Used for both validation errors and success confirmations across
 * all Village product forms on the page (product page + homepage).
 */
function showVillageToast(message, type) {
  let container = document.getElementById('village-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'village-toast-container';
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = `village-toast village-toast--${type === 'error' ? 'error' : 'success'}`;
  toast.textContent = message;
  container.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('is-visible'));

  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.remove(), 250);
  }, 3200);
}

/**
 * Village Signature Sweatshirt — product form behavior.
 * Handles: color/size variant matching, season + sleeve-text line item
 * properties, live price/availability updates, and AJAX add-to-cart.
 *
 * Expects a <product-form> custom element wrapping the form, with
 * variant data injected via a JSON script tag (id: village-variants-{sectionId}).
 */
class VillageProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.sectionId = this.dataset.sectionId;
    this.mode = this.dataset.mode === 'cart' ? 'cart' : 'redirect';
    this.productUrl = this.dataset.productUrl || '';
    this.variants = JSON.parse(document.getElementById(`village-variants-${this.sectionId}`).textContent);

    this.colorInputs = this.querySelectorAll('input[name="Color"]');
    this.sizeInputs = this.querySelectorAll('input[name="Size"]');
    this.seasonSelect = this.querySelector('[name="properties[Season]"]');
    this.sleeveInput = this.querySelector('[name="properties[Sleeve Text]"]');
    this.charCount = this.querySelector('.village-char-count');
    this.priceEl = this.querySelector('.village-price-line');
    this.errorEl = this.querySelector('.village-error');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.variantIdInput = this.querySelector('[name="id"]');

    this.colorInputs.forEach(el => el.addEventListener('change', () => this.syncVariant()));
    this.sizeInputs.forEach(el => el.addEventListener('change', () => this.syncVariant()));
    if (this.sleeveInput) {
      this.sleeveInput.addEventListener('input', () => this.updateCharCount());
      this.updateCharCount();
    }
    this.form.addEventListener('submit', (e) => this.onSubmit(e));

    this.syncVariant();
    this.applyFromUrl();
  }

  // On the real product page, pick up a color/size/season/sleeve-text
  // selection carried over via URL (e.g. from the homepage's "Make it
  // yours" form) and pre-fill the form with it. No-op everywhere else,
  // and no-op if the URL carries none of these params.
  applyFromUrl() {
    if (this.mode !== 'cart') return;

    const params = new URLSearchParams(window.location.search);
    const variantId = Number(params.get('variant'));

    if (!isNaN(variantId) && variantId) {
      const match = this.variants.find(v => v.id === variantId);
      if (match) {
        const [colorValue, sizeValue] = match.options;
        this.colorInputs.forEach(input => { input.checked = (input.value === colorValue); });
        this.sizeInputs.forEach(input => { input.checked = (input.value === sizeValue); });
        this.syncVariant();
      }
    }

    const season = params.get('Season');
    if (season && this.seasonSelect) {
      this.seasonSelect.value = season;
    }

    const sleeveText = params.get('Sleeve Text');
    if (sleeveText && this.sleeveInput) {
      this.sleeveInput.value = sleeveText;
      this.updateCharCount();
    }
  }

  getSelected(name) {
    const checked = this.querySelector(`input[name="${name}"]:checked`);
    return checked ? checked.value : null;
  }

  findMatch(color, size) {
    return this.variants.find(v =>
      v.options.some(o => o === color) && v.options.some(o => o === size)
    );
  }

  syncVariant() {
    const color = this.getSelected('Color');
    const size = this.getSelected('Size');

    // Disable sizes with no stock for the chosen color.
    this.sizeInputs.forEach(input => {
      const match = this.findMatch(color, input.value);
      const label = input.nextElementSibling;
      const available = match ? match.available : true;
      label.classList.toggle('is-disabled', !available);
      input.disabled = !available;
    });

    const match = color && size ? this.findMatch(color, size) : null;

    if (match) {
      this.variantIdInput.value = match.id;
      this.priceEl.textContent = this.formatMoney(match.price);
      this.submitBtn.disabled = !match.available;
      this.submitBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
    } else {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Select Color & Size';
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  updateCharCount() {
    if (!this.charCount) return;
    const max = 30;
    const len = this.sleeveInput.value.length;
    this.charCount.textContent = `${len}/${max}`;
    if (len > max) {
      this.sleeveInput.value = this.sleeveInput.value.slice(0, max);
    }
  }

  showError(message) {
    showVillageToast(message, 'error');
  }

  clearError() {
    // No-op now — toasts auto-dismiss on their own. Kept so existing
    // calls to clearError() elsewhere don't need to change.
  }

  async onSubmit(e) {
    e.preventDefault();
    this.clearError();

    if (!this.variantIdInput.value) {
      this.showError('Please choose a color and size.');
      return;
    }

    const season = this.seasonSelect ? this.seasonSelect.value : '';
    if (this.seasonSelect && season === '') {
      this.showError('Please choose your season.');
      return;
    }

    if (this.mode === 'redirect') {
      const sleeveText = this.sleeveInput ? this.sleeveInput.value : '';
      const target = `${this.productUrl}?variant=${encodeURIComponent(this.variantIdInput.value)}`
        + `&Season=${encodeURIComponent(season)}`
        + `&Sleeve%20Text=${encodeURIComponent(sleeveText)}`;
      window.location.href = target;
      return;
    }

    this.submitBtn.disabled = true;
    const originalLabel = this.submitBtn.textContent;
    this.submitBtn.textContent = 'Adding…';

    const formData = new FormData(this.form);

    try {
      const res = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add this item to your cart.');
      }
      document.dispatchEvent(new CustomEvent('village:added-to-cart', { bubbles: true }));
      showVillageToast('Added to your cart.', 'success');
      this.submitBtn.textContent = 'Added ✓';
      setTimeout(() => { this.submitBtn.textContent = originalLabel; this.submitBtn.disabled = false; }, 1800);
    } catch (err) {
      this.showError(err.message);
      this.submitBtn.textContent = originalLabel;
      this.submitBtn.disabled = false;
    }
  }
}

if (!customElements.get('village-product-form')) {
  customElements.define('village-product-form', VillageProductForm);
}
